/**
 * Canonical diagnostic pillar order. Both the overview landing and the
 * wizard navigate through pillars in this order, matching the backend's
 * `ORDER BY code ASC` sort in `getDiagnosticQuestionsByPillar`
 * (supabase/functions/_shared/prometheus/bss-diagnostic.ts).
 */

export const PILLAR_ORDER = [
  'culture',
  'finance',
  'management',
  'marketing',
  'operations',
  'sales',
] as const;

export type PillarCode = typeof PILLAR_ORDER[number];

export const PILLAR_CODE_SET: ReadonlySet<string> = new Set<string>(PILLAR_ORDER);

export function isPillarCode(value: unknown): value is PillarCode {
  return typeof value === 'string' && PILLAR_CODE_SET.has(value);
}

export function indexOfPillar(code: string): number {
  return PILLAR_ORDER.indexOf(code as PillarCode);
}

/**
 * Stable sort for pillar maps returned by the backend. Pillars appear in
 * canonical order first; anything not in `PILLAR_ORDER` keeps its relative
 * source order and tails the known codes.
 */
export function orderPillars<T extends { pillar: { code: string } }>(
  raw: Record<string, T> | undefined,
): T[] {
  if (!raw) return [];
  const byCode = new Map<string, T>();
  for (const [code, value] of Object.entries(raw)) {
    byCode.set(code, value);
  }
  const ordered: T[] = [];
  for (const code of PILLAR_ORDER) {
    const hit = byCode.get(code);
    if (hit) {
      ordered.push(hit);
      byCode.delete(code);
    }
  }
  ordered.push(...byCode.values());
  return ordered;
}
